import { motion } from 'framer-motion';
import { TREE_COLORS } from './useTreeGrowth';

/** Exported type for leaf color variants */
export type LeafVariant = 'primary' | 'shadow' | 'highlight';

/** Exported interface for leaf position data */
export interface LeafPosition {
  cx: number;
  cy: number;
  size: number;
  rotation: number;
  variant: LeafVariant;
}

interface LeafProps {
  /** X position relative to canopy center */
  cx: number;
  /** Y position relative to canopy center */
  cy: number;
  /** Leaf size (radius) */
  size: number;
  /** Animation delay for staggered appearance */
  delay: number;
  /** Rotation angle for variety */
  rotation?: number;
  /** Color variant: 'primary' | 'shadow' | 'highlight' */
  variant?: 'primary' | 'shadow' | 'highlight';
}

/**
 * Individual animated leaf component
 * Appears with spring pop-in animation and gentle continuous sway
 */
export function Leaf({
  cx,
  cy,
  size,
  delay,
  rotation = 0,
  variant = 'primary'
}: LeafProps) {
  const color = variant === 'shadow'
    ? TREE_COLORS.leafShadow
    : variant === 'highlight'
      ? TREE_COLORS.leafHighlight
      : TREE_COLORS.leaf;

  // Slight variation in sway timing per leaf for natural look
  const swayDuration = 3 + (rotation % 60) / 30; // 3-5 seconds
  const swayAmount = 2 + (size % 3); // 2-5 degrees

  return (
    <motion.ellipse
      cx={cx}
      cy={cy}
      rx={size * 0.8}
      ry={size * 1.2}
      fill={color}
      style={{ transformOrigin: `${cx}px ${cy}px` }}
      initial={{ scale: 0, opacity: 0, rotate: rotation }}
      animate={{
        scale: 1,
        opacity: 1,
        rotate: [rotation - swayAmount, rotation + swayAmount, rotation - swayAmount],
      }}
      transition={{
        scale: {
          type: 'spring',
          stiffness: 200,
          damping: 15,
          delay: delay,
        },
        opacity: {
          type: 'spring',
          stiffness: 200,
          damping: 15,
          delay: delay,
        },
        rotate: {
          duration: swayDuration,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: delay + 0.3, // Start sway after pop-in
        },
      }}
    />
  );
}
